/**
 * Google Tag Manager / GA4 analytics for the Jack Wolfskin theme.
 * Reads page, shop, and cart config from `window.GTMConfig` (set in `snippets/gtm-valantic.liquid`).
 * Logged-in `accountId` / `crmId` come from `#gtm-customer-ids` (JSON), consumed once for `pageview`, not stored globally.
 * Loaded as `type="module"` with `fetchpriority="low"` so parsing does not block the main thread.
 *
 * Voyado-owned events (`filter`, `sorting`, `onsite_search`, `search_flyout_*`, etc.) live in `src/voyado-app/`.
 *
 * @module gtm-valantic
 */

const cfg = /** @type {import('./gtm-valantic').GTMConfig} */ (window.GTMConfig);
if (!cfg) throw new Error('[GTM] window.GTMConfig not found');

/** @constant {string} */
const GTM_AFFILIATION = cfg.affiliation;

/** @constant {string} */
const GTM_BUSINESS_VERTICAL = cfg.businessVertical;

/** @constant {{ item_list_id: string, item_list_name: string }} */
const CART_LIST_CONTEXT = cfg.cartListContext;

/** Active shop currency (Shopify global overrides Liquid default). */
const CURRENCY = window?.Shopify?.currency?.active ?? cfg.currency;

/** Shopify `template.name` from Liquid (e.g. `product`, `cart`, `index`). */
const PAGE_TYPE = cfg.pageType;

/**
 * Reads logged-in customer ids from `#gtm-customer-ids`, removes the node (not kept on `window`).
 * @returns {{ accountId: string, crmId: string }}
 */
function consumeCustomerIds() {
  const el = document.getElementById('gtm-customer-ids');
  if (!el) return {accountId: '', crmId: ''};

  let data;
  try {
    data = JSON.parse(el.textContent || '{}');
  } catch {
    console.warn('[GTM] Invalid customer ids JSON');
    el.remove();
    return {accountId: '', crmId: ''};
  }

  el.remove();
  return {
    accountId: String(data.accountId ?? ''),
    crmId: String(data.crmId ?? ''),
  };
}

/** One-time customer ids for the initial pageview (not on `window.GTMConfig`). */
const customerIds = consumeCustomerIds();

/**
 * Maps Shopify template names to GA4 `page.type` values.
 * @type {Record<string, string>}
 */
const PAGE_TYPE_MAP = {
    404: '404',
    article: 'article',
    blog: 'blog',
    cart: 'cart',
    collection: 'categorypage',
    gift_card: 'gift_card',
    index: 'homepage',
    product: 'product',
    search: 'searchresults',
    'customers/login': 'login',
    'customers/register': 'sign_up',
};

/**
 * Selectors used to find clickable controls inside popups for `popup_cta`.
 * @constant {string[]}
 */
const BUTTON_SELECTORS = [
    'button',
    '.text-button',
    '[role="button"]',
    'input[type="button"]',
    'input[type="submit"]',
];

/** Matches Shopify Cart API mutation endpoints intercepted by `patchFetch`. */
const cartRegex = /\/cart\/(add|change|update)(\.js|\.json)?(\?.*)?$/;

/**
 * Indexes cart lines by a stable identity for diffing.
 *
 * Shopify's line `key` is `{variant_id}:{md5(line properties)}`. In this store
 * the properties hash is rewritten on every quantity change, so `key` is NOT
 * stable across mutations — diffing on it makes one line look like a removal of
 * the old key plus an add of the new key. We anchor on the variant `id` (stable
 * in both the Liquid seed and `/cart.js`) and disambiguate duplicate-variant
 * lines by their occurrence order.
 *
 * @param {{ id?: number, key?: string }[]} items
 * @returns {Map<string, Record<string, unknown>>}
 */
function indexCartLines(items) {
    const map = new Map();
    const counts = new Map();
    for (const line of items || []) {
        const variantId = String(line?.id ?? line?.key ?? '');
        const n = counts.get(variantId) ?? 0;
        counts.set(variantId, n + 1);
        map.set(n === 0 ? variantId : `${variantId}#${n}`, line);
    }
    return map;
}

/** `sessionStorage` key holding the pre-mutation cart snapshot. */
const PENDING_CART_EVENT = 'gtm_pending_cart_event';

/** Slim cart snapshot seeded from Liquid on first paint. */
let shopifyCart = slimCartSnapshot(cfg.shopifyCart);

/** Debounce timer for coalescing rapid `/cart/*` responses. */
let cartUpdateTimeout = null;

/** True while a mutation's debounced `/cart.js` refresh is scheduled or in flight. */
let cartRefreshPending = false;

/** Callbacks waiting for the in-flight cart refresh to settle (see `onCartSettled`). */
let cartSettleCallbacks = [];

/**
 * Runs `callback` once the drawer's DOM has a `[data-line-index]` block per `shopifyCart` line.
 * The theme re-renders the drawer's cart lines via its own section morph — a separate async
 * process from our `/cart.js` refresh — so `shopifyCart` data can be current before that markup
 * lands. Reacts to the actual DOM mutation via `MutationObserver` (the morph exposes no public
 * "done" event to hook into) instead of polling; `timeoutMs` is only a circuit breaker in case
 * the expected markup never arrives (e.g. a failed morph), so this can't hang indefinitely.
 * @param {() => void} callback
 * @param {number} [timeoutMs]
 */
function waitForCartDrawerDom(callback, timeoutMs = 3000) {
    const expected = shopifyCart?.items?.length ?? 0;
    if (document.querySelectorAll('[data-line-index]').length >= expected) {
        callback();
        return;
    }

    const finish = () => {
        observer.disconnect();
        clearTimeout(timeoutId);
        callback();
    };
    const observer = new MutationObserver(() => {
        if (document.querySelectorAll('[data-line-index]').length >= expected) finish();
    });
    observer.observe(document.body, {childList: true, subtree: true});
    const timeoutId = setTimeout(finish, timeoutMs);
}

/**
 * Runs `callback` once any pending cart-mutation refresh settles — immediately if none is
 * pending. Used so the cart drawer's `view_cart` (fired on open) doesn't read a stale, empty
 * `shopifyCart` when the drawer auto-opens right as an add/remove is still in flight.
 * @param {() => void} callback
 */
function onCartSettled(callback) {
    if (cartRefreshPending) {
        cartSettleCallbacks.push(() => waitForCartDrawerDom(callback));
    } else {
        waitForCartDrawerDom(callback);
    }
}

/**
 * Cart-page-only: `#gtm-cart-line-*` JSON captured at remove-button click time, keyed by
 * cart line `key`. Entries are read once by `normalizeCartLine` and deleted immediately after
 * — this is not a standing cache, just a same-tick handoff from click to the eventual diff,
 * needed because the theme's own section morph removes that DOM node as soon as the click's
 * own cart mutation resolves (before our debounced `/cart.js` diff gets a chance to read it).
 * Entries carry a capture timestamp: if the mutation never resolves (e.g. a network error), the
 * entry would otherwise never be read/deleted and leak for the rest of the session —
 * `pruneStaleClickCaptures` clears anything left over past `CLICK_CAPTURE_TTL_MS`.
 * @type {Map<string, { data: Record<string, unknown>, capturedAt: number }>}
 */
const clickCapturedLineData = new Map();

/** Longest a click-captured line waits for its mutation to resolve before being abandoned (ms). */
const CLICK_CAPTURE_TTL_MS = 10000;

/** Drops click-captured entries whose mutation never resolved within `CLICK_CAPTURE_TTL_MS`. */
function pruneStaleClickCaptures() {
    const cutoff = Date.now() - CLICK_CAPTURE_TTL_MS;
    for (const [key, entry] of clickCapturedLineData) {
        if (entry.capturedAt < cutoff) clickCapturedLineData.delete(key);
    }
}

/**
 * Resolves a Shopify template name to the GA4 page type string.
 * @param {string} value - `template.name` from Liquid
 * @returns {string}
 */
function getPageType(value) {
    return PAGE_TYPE_MAP[value] || 'other';
}

/**
 * Breakpoint label for `site.mediaquery` on pageview.
 * @returns {'small' | 'medium' | 'large'}
 */
function getCurrentBreakpoint() {
    const w = window.innerWidth;
    return w <= 600 ? 'small' : w <= 960 ? 'medium' : 'large';
}

/**
 * Converts Shopify integer cents to a major-unit float with two decimals.
 * @param {number} price - Price in cents
 * @returns {number}
 */
function formatPrice(price) {
    return +(price / 100).toFixed(2);
}

/**
 * Style-color GA4 item id (`A65257_6350`) from a full SKU (`A65257_6350_A06`) — the first two
 * underscore-separated segments. The size/variant segment is dropped: it's already carried by
 * `item_variant`, and the full SKU stays available separately as `sku`.
 * @param {string} sku
 * @returns {string}
 */
function styleColorFromSku(sku) {
    if (typeof sku !== 'string' || !sku) return '';
    return sku.split('_').slice(0, 2).join('_');
}

/**
 * Human-readable variant label from cart line or static GTM JSON.
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
function getItemVariantLabel(item) {
    const variantTitle = item?.variant_title;
    if (typeof variantTitle === 'string' && variantTitle) return variantTitle;
    const opts = item?.options_with_values;
    if (Array.isArray(opts) && opts.length) {
        return opts.map((o) => o.value).filter(Boolean).join(' / ');
    }
    if (Array.isArray(item?.variant_options) && item.variant_options.length) {
        return item.variant_options.join(' / ');
    }
    return typeof item?.item_variant === 'string' ? item.item_variant : '';
}

/**
 * Normalizes Shopify discount allocation objects (line-level vs cart-level).
 * @param {Record<string, unknown>} allocation
 * @returns {Record<string, unknown>}
 */
function resolveAllocation(allocation) {
    return allocation?.discount_application ?? allocation ?? {};
}

/**
 * Comma-separated discount codes applied to a cart line, or `-` when none.
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
function getLineItemCoupon(item) {
    const codes = (item?.line_level_discount_allocations || [])
        .map(resolveAllocation)
        .filter((d) => d?.type === 'discount_code' && d?.title)
        .map((d) => d.title);
    return codes.length ? [...new Set(codes)].join(', ') : '';
}

/**
 * Discount amount, original unit price, and discount percentage for a cart line.
 * @param {Record<string, unknown>} item
 * @returns {{ discount: number, original_price: number, discount_percentage: number }}
 */
function getLineDiscountFields(item) {
    const finalCents = item?.final_price ?? item?.discounted_price ?? item?.price ?? 0;
    const originalCents = Math.max(
        finalCents,
        item?.compare_at_price ?? 0,
        item?.original_unit_price ?? 0,
        item?.original_price ?? 0,
    );
    const discountCents = originalCents - finalCents;
    return {
        discount: formatPrice(discountCents),
        original_price: formatPrice(originalCents),
        discount_percentage: originalCents > 0 ? Math.round((discountCents / originalCents) * 100) : 0,
    };
}

/**
 * Cart or static product JSON → full GA4 `items[]` object.
 * @param {Record<string, unknown>} item
 * @param {number} [index]
 * @param {{ item_list_id?: string, item_list_name?: string }} [listContext]
 * @returns {Record<string, unknown>}
 */
function normalizeGtmItem(item, listContext = CART_LIST_CONTEXT) {
    if (!item) return {};
    const normalized = {
        ...getLineDiscountFields(item),
        item_name: item.item_name || item.product_title || item.title,
        item_id: styleColorFromSku(item.sku) || item.item_id || String(item.product_id || ''),
        sku: item.sku || '',
        affiliation: GTM_AFFILIATION,
        coupon: item.coupon ?? getLineItemCoupon(item),
        price: formatPrice(item.final_price ?? item.price),
        item_variant: getItemVariantLabel(item),
        item_brand: item.item_brand || item.vendor || '',
        item_list_id: item.item_list_id || listContext.item_list_id || CART_LIST_CONTEXT.item_list_id,
        item_list_name: item.item_list_name || listContext.item_list_name || CART_LIST_CONTEXT.item_list_name,
        quantity: item.quantity ?? 1,
        stock: Boolean(item.stock ?? item.variant_inventory_quantity ?? false),
        category_list: [...new Set(item.category_list || [])],
        google_business_vertical: GTM_BUSINESS_VERTICAL,
        parent_id: item.parent_id || (item.sku ? String(item.sku).split('_')[0] : ''),
        item_category: item.item_category || '',
        item_category2: item.item_category2 || '',
        item_category3: item.item_category3 || '',
        item_category4: item.item_category4 || '',
        item_type: item.item_type || '',
    };
    return normalized;
}

/**
 * Subtotal in cents from cart payload (falls back to summing line totals).
 * @param {Record<string, unknown>} cart
 * @returns {number}
 */
function getCartSubtotalCents(cart) {
    return (
        cart?.items_subtotal_price ??
        (cart?.items || []).reduce(
            (sum, line) => sum + (line.final_price ?? line.price ?? 0) * (line.quantity || 1),
            0,
        )
    );
}

/**
 * `value`, `net_value`, and `total_quantity` for CDP cart payloads.
 * @param {Record<string, unknown>} cart
 * @returns {{ value: number, net_value: number, total_quantity: number }}
 */
function getCartValueFields(cart) {
    return {
        value: formatPrice(cart?.total_price ?? cart?.value ?? 0),
        net_value: formatPrice(getCartSubtotalCents(cart)),
        total_quantity: cart?.item_count ?? (cart?.items || []).reduce((n, line) => n + (line.quantity || 0), 0),
    };
}

/**
 * Minimal cart state kept in memory and `sessionStorage` for diffing.
 * @param {Record<string, unknown> | null | undefined} cart
 * @returns {{ currency?: string, total_price?: number, items_subtotal_price?: number, item_count?: number, items: unknown[] }}
 */
function slimCartSnapshot(cart) {
    if (!cart) return {items: []};
    return {
        currency: cart.currency,
        total_price: cart.total_price,
        items_subtotal_price: cart.items_subtotal_price,
        item_count: cart.item_count,
        items: (cart.items || []).map((line) => ({
            key: line.key,
            id: line.id,
            product_id: line.product_id,
            sku: line.sku || '',
            quantity: line.quantity,
            final_price: line.final_price ?? line.price,
            product_title: line.product_title || '',
            variant_title: line.variant_title || '',
            vendor: line.vendor || '',
        })),
    };
}

/**
 * Style ID: first SKU segment before `_`, or `product_id` when SKU is empty.
 * @param {{ sku?: string, product_id?: number }} line
 * @returns {string}
 */
function cartLineStyleId(line) {
    const sku = line.sku || '';
    return sku ? sku.split('_')[0] : String(line.product_id || '');
}

/**
 * CDP cart payload for `ecommerce.cart` on add/remove — no DOM lookups.
 * `variant_list` entries: `item_id` (SKU), `parent_id` (style), `quantity`, `price`.
 *
 * @param {Record<string, unknown>} cart
 * @returns {Record<string, unknown>}
 */
function buildCartCdpEcommerce(cart) {
    const lines = cart?.items || [];
    const variantList = lines.map((line) => ({
        item_id: line.sku || String(line.product_id || ''),
        parent_id: cartLineStyleId(line),
        quantity: line.quantity ?? 1,
        price: formatPrice(line.final_price ?? line.price ?? 0),
    }));
    const styleIds = lines.map(cartLineStyleId).filter(Boolean);
    return {
        ...getCartValueFields(cart),
        item_list: variantList.map((l) => ({item_id: l.parent_id, quantity: l.quantity})),
        item_ids: [...new Set(styleIds)],
        variant_list: variantList,
        variant_ids: variantList.map((l) => l.item_id).filter(Boolean),
    };
}

/**
 * Parses pre-rendered GTM JSON from a DOM node (typically `<script type="application/json">`).
 * @param {Element | null} el
 * @param {string} [warnLabel] - Context for console.warn on parse failure
 * @returns {Record<string, unknown> | null}
 */
function parseGtmJsonElement(el, warnLabel = 'GTM JSON') {
    if (!(el instanceof Element)) return null;
    try {
        const raw = (el.textContent || '').trim();
        if (!raw || /Liquid error/i.test(raw)) return null;
        return JSON.parse(raw);
    } catch (e) {
        console.warn(`[GTM] Invalid JSON in ${warnLabel}`, e);
        return null;
    }
}

/**
 * Parses the header menu hierarchy JSON — rendered by `sections/breadcrumbs.liquid` on any
 * template that section is placed on (product, collection, and cart). Null if absent/invalid,
 * e.g. the cart drawer opened from a page without that section.
 * @returns {{ items?: Array<{ title?: string, url?: string, items?: unknown[] }> } | null}
 */
function getHeaderMenuHierarchy() {
    return parseGtmJsonElement(document.querySelector('script[data-header-menu-hierarchy]'), 'header-menu-hierarchy');
}

/**
 * Collection handle from a `/collections/...` URL, e.g. `/nl-nl/collections/dames` -> `dames`.
 * @param {string} url
 * @returns {string}
 */
function collectionHandleFromUrl(url) {
    return (String(url || '').match(/\/collections\/([^/?#]+)/i) || [])[1] || '';
}

/**
 * Titles from the menu root down to the first node (any depth) whose collection handle matches,
 * or `[]`.
 * @param {Array<{ title?: string, url?: string, items?: unknown[] }>} items
 * @param {string} targetHandle
 * @param {string[]} [trail]
 * @returns {string[]}
 */
function findMenuPathByHandle(items, targetHandle, trail = []) {
    for (const item of items || []) {
        if (!item || typeof item !== 'object') continue;
        const title = typeof item.title === 'string' ? item.title : '';
        const nextTrail = title ? [...trail, title] : trail;
        if (collectionHandleFromUrl(item.url) === targetHandle) return nextTrail;
        const found = findMenuPathByHandle(item.items, targetHandle, nextTrail);
        if (found.length) return found;
    }
    return [];
}

/**
 * Resolves `item_list_id`/`item_list_name` for a product from its own collection handles
 * (`candidateHandles`), preferring the first one actually linked in the site menu — mirrors the
 * same preference used for PDP breadcrumbs (`sections/breadcrumbs.liquid`). The display name comes
 * from the matched menu entry's own title, not the collection's title, so it agrees with the PDP
 * breadcrumb text for the same product.
 * Null when the menu JSON is unavailable or no candidate matches — caller keeps its static default.
 * @param {string[]} candidateHandles
 * @returns {{ item_list_id: string, item_list_name: string } | null}
 */
function resolveMenuListContext(candidateHandles) {
    if (!Array.isArray(candidateHandles) || !candidateHandles.length) return null;
    const menu = getHeaderMenuHierarchy();
    if (!menu?.items) return null;

    for (const handle of candidateHandles) {
        if (!handle) continue;
        const path = findMenuPathByHandle(menu.items, handle);
        if (path.length) return {item_list_id: handle, item_list_name: path[path.length - 1]};
    }
    return null;
}

/**
 * Overrides `item_list_id`/`item_list_name` on a GA4 item using `resolveMenuListContext` against
 * its own `list_candidates` (added by `snippets/gtm-cart-line.liquid`), then strips that field —
 * it's a resolution input, not a GA4 property. No-op (beyond the strip) when nothing matches,
 * leaving the static default in place (`product.collections.first`).
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>} The same `item` reference, mutated.
 */
function applyMenuListContext(item) {
    const resolved = resolveMenuListContext(item.list_candidates);
    if (resolved) {
        item.item_list_id = resolved.item_list_id;
        item.item_list_name = resolved.item_list_name;
    }
    delete item.list_candidates;
    return item;
}

/**
 * Reads pre-rendered GTM JSON from a hidden `.gtm-card-product` node or `#gtm-product-{id}`.
 *
 * @param {Node | number | string} product - DOM node, or product id for lookup
 * @returns {Record<string, unknown> | null}
 */
function getGtmData(product) {
    let node = null;
    if (product instanceof Node) {
        node = product.querySelector('.gtm-card-product');
    } else if (typeof product === 'number' || typeof product === 'string') {
        node = document.querySelector(`#gtm-product-${product}`);
    }
    if (!node) return null;
    return parseGtmJsonElement(node, '.gtm-card-product');
}

/**
 * Reads pre-rendered GTM JSON for a cart line from `#gtm-cart-line-{productId}-{variantId}-*`
 * (rendered on the cart page only). Fallback for add/remove events triggered from the cart page,
 * where no `#gtm-product-{id}` card exists to enrich category/stock data from.
 *
 * @param {number | string} productId
 * @param {number | string} variantId
 * @returns {Record<string, unknown> | null}
 */
function getGtmCartLineDataByVariant(productId, variantId) {
    const node = document.querySelector(`[id^="gtm-cart-line-${productId}-${variantId}-"]`);
    return parseGtmJsonElement(node, `gtm-cart-line-${productId}-${variantId}`);
}

/**
 * Single cart mutation line for `ecommerce.items` on add/remove events.
 *
 * @param {Record<string, unknown>} line
 * @param {number} [quantity] - Mutation quantity (may differ from line.quantity)
 * @returns {Record<string, unknown>}
 */
function normalizeCartLine(line, quantity) {
    const capturedEntry = line.key ? clickCapturedLineData.get(line.key) : undefined;
    if (capturedEntry) clickCapturedLineData.delete(line.key);
    const staticGtmData = applyMenuListContext(
        capturedEntry?.data ||
        getGtmData(line.product_id) ||
        getGtmData(line.id) ||
        getGtmCartLineDataByVariant(line.product_id, line.id) ||
        {},
    );
    const listContext = {
        item_list_id: staticGtmData.item_list_id || CART_LIST_CONTEXT.item_list_id,
        item_list_name: staticGtmData.item_list_name || CART_LIST_CONTEXT.item_list_name,
    };
    const compareAtPriceCents = line.compare_at_price ?? staticGtmData.original_price ?? undefined;
    const base = {...staticGtmData, ...line, compare_at_price: compareAtPriceCents};
    const payload = quantity != null ? {...base, quantity} : base;
    const normalized = normalizeGtmItem(payload, listContext);

    // Mutating this same line's product on its own PDP: reuse the resolved breadcrumb list
    // context so add_to_cart/remove_from_cart agree with view_item's item_list_id/name, instead of
    // the static per-card default (product.collections.first).
    const isCurrentPdpProduct = PAGE_TYPE === 'product' && String(line.product_id) === String(cfg.productId);
    return isCurrentPdpProduct ? applyBreadcrumbListContext(normalized) : normalized;
}

/** Clears the previous `ecommerce` object before pushing a new GA4 ecommerce event. */
function resetEcommerce() {
    window.dataLayer.push({ecommerce: null});
}

/**
 * Pushes `text_message` when theme surfaces a user-visible message.
 * @param {unknown} message
 */
function pushTextMessage(message) {
    const text = typeof message === 'string' ? message.trim() : '';
    if (!text) return;
    window.dataLayer.push({event: 'text_message', message_text: text});
}

/**
 * Newsletter success/error events from `theme:newsletter_*` custom events.
 *
 * @param {'newsletter_signup' | 'newsletter_error'} eventName
 * @param {{ form_id?: string, email?: string }} [detail]
 */
function pushNewsletterEvent(eventName, detail) {
    const {form_id: formId, email} = detail || {};
    if (!formId) return;
    if (eventName === 'newsletter_signup' && !email) return;
    const payload = {event: eventName, form_id: formId};
    if (email) payload.email = email;
    window.dataLayer.push(payload);
}

/**
 * Member (loyalty) signup event from a `theme:member_signup` custom event.
 *
 * @param {{ form_id?: string, email?: string }} [detail]
 */
function pushMemberSignupEvent(detail) {
    const {form_id: formId, email} = detail || {};
    if (!formId || !email) return;
    window.dataLayer.push({event: 'member_signup', form_id: formId, email});
}

/**
 * Wraps `window.fetch` to detect cart mutations and schedule a `/cart.js` refresh.
 */
function patchFetch() {
    const previousFetch = window.fetch;
    window.fetch = async function (...args) {
        const [resource] = args;
        try {
            if (typeof resource !== 'string') return previousFetch.apply(this, args);
            if (cartRegex.test(resource)) {
                // Set before awaiting: the cart drawer can open synchronously on the same click
                // that triggers this fetch, before the response (and `monitorCartUpdate`) land.
                cartRefreshPending = true;
                const response = await previousFetch.apply(this, args);
                if (response.ok) {
                    sessionStorage.setItem(PENDING_CART_EVENT, JSON.stringify(slimCartSnapshot(shopifyCart)));
                    monitorCartUpdate();
                } else {
                    console.warn(`[GTM] Fetch ${resource} failed: ${response.status}`);
                    cartRefreshPending = false;
                }
                return response;
            }
        } catch (error) {
            console.error('[GTM] Fetch Wrapper Error:', error);
            cartRefreshPending = false;
        }
        return previousFetch.apply(this, args);
    };
}

/** Debounced cart refresh after a mutation response. */
function monitorCartUpdate() {
    cartRefreshPending = true;
    if (cartUpdateTimeout) clearTimeout(cartUpdateTimeout);
    cartUpdateTimeout = setTimeout(fetchCartData, 200);
}

/** Fetches full cart JSON and diffs against the pending snapshot. */
async function fetchCartData() {
    try {
        const response = await fetch('/cart.js?adw=1');
        if (response.ok) {
            compareCartData(await response.json());
        } else {
            console.error('[GTM] Failed to fetch /cart.js:', response.status);
        }
    } catch (error) {
        console.error('[GTM] Error fetching /cart.js:', error);
    } finally {
        cartRefreshPending = false;
        pruneStaleClickCaptures();
        const callbacks = cartSettleCallbacks;
        cartSettleCallbacks = [];
        callbacks.forEach((callback) => callback());
    }
}

/**
 * Compares old and new cart lines and fires `add_to_cart` / `remove_from_cart`.
 *
 * @param {Record<string, unknown>} newCartData
 */
function compareCartData(newCartData) {
    const pendingSnapshot = sessionStorage.getItem(PENDING_CART_EVENT);
    const oldCartData = pendingSnapshot ? JSON.parse(pendingSnapshot) : {items: []};
    newCartData = newCartData || {items: []};
    const oldItems = indexCartLines(oldCartData.items);
    const newItems = indexCartLines(newCartData.items);

    newItems.forEach((newItem, key) => {
        const oldItem = oldItems.get(key);
        if (!oldItem) {
            pushCartMutationEvent('add_to_cart', newItem.quantity, newItem, newCartData);
        } else if (newItem.quantity > oldItem.quantity) {
            pushCartMutationEvent('add_to_cart', newItem.quantity - oldItem.quantity, newItem, newCartData);
        }
    });

    oldItems.forEach((oldItem, key) => {
        const newItem = newItems.get(key);
        if (!newItem) {
            pushCartMutationEvent('remove_from_cart', oldItem.quantity, oldItem, newCartData);
        } else if (oldItem.quantity > newItem.quantity) {
            pushCartMutationEvent('remove_from_cart', oldItem.quantity - newItem.quantity, newItem, newCartData);
        }
    });

    sessionStorage.removeItem(PENDING_CART_EVENT);
    shopifyCart = slimCartSnapshot(newCartData);
}

/**
 * Pushes add/remove with one GA4 item and full CDP cart snapshot.
 *
 * @param {'add_to_cart' | 'remove_from_cart'} eventName
 * @param {number} quantity
 * @param {Record<string, unknown>} item
 * @param {Record<string, unknown>} cartState
 */
function pushCartMutationEvent(eventName, quantity, item, cartState) {
    const mutationItem = normalizeCartLine(item, quantity);
    const unitPrice = mutationItem.price ?? formatPrice(item.final_price ?? item.price);
    resetEcommerce();
    window.dataLayer.push({
        event: eventName,
        ecommerce: {
            currency: cartState?.currency || CURRENCY,
            value: +(unitPrice * quantity).toFixed(2),
            items: [mutationItem],
            cart: buildCartCdpEcommerce(cartState),
        },
    });
}

/**
 * Collapses whitespace in element text for navigation labels.
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function trimText(el) {
    return (el?.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Best-effort label for buttons and footer links (`navigation_text`, popups).
 * @param {Element} el
 * @returns {string}
 */
function getElText(el) {
    return (
        trimText(el) ||
        el.dataset.navText ||
        el.getAttribute('aria-label') ||
        el.getAttribute('title') ||
        el.querySelector('[data-content]')?.dataset.content ||
        el.getAttribute('class') ||
        trimText(el.parentNode)
    );
}

/**
 * Stable popup identifier for dialog and overlay events.
 * @param {Element} dialog
 * @returns {string}
 */
function getDialogId(dialog) {
    return dialog.getAttribute('aria-labelledby') || dialog.getAttribute('class');
}

/**
 * @param {'popup_visible' | 'popup_close' | 'popup_cta'} eventType
 * @param {string} id
 */
function setDataLayerDialog(eventType, id) {
    window.dataLayer.push({event: eventType, popup_id: id});
}

/**
 * @param {'popup_visible'} eventType
 * @param {string} id
 */
function setDataLayerPopUp(eventType, id) {
    if (id) window.dataLayer.push({event: eventType, popup_id: id});
}

/**
 * @param {Element | null} popUp
 * @returns {boolean}
 */
function isVisualPopUp(popUp) {
    if (!popUp) return false;
    const cs = window.getComputedStyle(popUp);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
}

/**
 * Resolves an id for cookie banner, quick-add, or theme dialogs.
 * @param {Element} popUp
 * @returns {string}
 */
function getIdPopUp(popUp) {
    return (
        popUp.id ||
        popUp.closest('[id]')?.id ||
        popUp.querySelector('form[data-id]')?.dataset.id ||
        popUp.querySelector('form[data-testid]')?.dataset.testid ||
        popUp.getAttribute('class') ||
        'unknown_popup'
    );
}

/** Tags buttons inside a dialog so global click handler can emit `popup_cta`. */
function markButtons(popUp) {
    popUp.querySelectorAll(BUTTON_SELECTORS).forEach((el) => {
        el.dataset.layerPopupCta = getDialogId(popUp);
    });
}

/**
 * Waits for an element via `MutationObserver`. Optional `onChange` runs once on attribute change, then disconnects.
 * Resolves `null` (and disconnects the observer) after `timeoutMs` if the element never appears —
 * without this, an element that never shows up (e.g. a consent banner blocked/never configured)
 * would leave the observer watching the whole `root` subtree for the rest of the page's life.
 *
 * @param {string | Node} selector
 * @param {{ root?: Element, onChange?: (el: Element) => void, timeoutMs?: number }} [options]
 * @returns {Promise<Element | null>}
 */
function observerHandler(selector, {root = document, onChange, timeoutMs = 10000} = {}) {
    let visitedEl = false;
    return new Promise((resolve) => {
        let resolved = false;
        let timeoutId;
        const predicate = selector instanceof Node ? () => selector : () => root.querySelector(selector);

        const finish = (el) => {
            if (resolved) return;
            resolved = true;
            clearTimeout(timeoutId);
            try {
                domObserver.disconnect();
            } catch {
                /* observer may already be disconnected */
            }
            if (el && typeof onChange === 'function') {
                const attrObserver = new MutationObserver(() => {
                    if (visitedEl) return;
                    visitedEl = true;
                    onChange(el);
                    attrObserver.disconnect();
                });
                attrObserver.observe(el, {
                    attributes: true,
                    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
                });
            }
            resolve(el);
        };

        const immediate = predicate();
        if (immediate) return finish(immediate);

        const domObserver = new MutationObserver(() => {
            const el = predicate();
            if (el) finish(el);
        });
        domObserver.observe(root, {childList: true, subtree: true});
        timeoutId = setTimeout(() => finish(null), timeoutMs);
    });
}

/**
 * Current pathname relative to the shop's locale root (e.g. `/nl-nl/products/x` -> `/products/x`),
 * or `/` for the root itself.
 * @returns {string}
 */
function resolvePagePath() {
    const root = window.Shopify?.routes?.root || '/';
    const path = window.location.pathname;
    if (path === root.slice(0, -1)) return '/';
    return path.startsWith(root) ? '/' + path.slice(root.length) : path;
}

/** Pushes initial `pageview` with user, site, and page context from `GTMConfig`. */
function pushDataLayerEvents() {
    const {customer, site} = cfg;
    const user = {
        logged_in: customer.loggedIn,
        type: customer.type,
    };

    if (customer.loggedIn) {
        user.loyalty_status = customer.loyaltyStatus;
        user.account_id = customerIds.accountId;
        user.crm_id = customerIds.crmId;
    }

    window.dataLayer.push({
        event: 'pageview',
        user,
        application: {
            id: 'main',
            store: site.store,
            version: '3.4.0',
        },
        site: {
            language: site.language,
            environment: site.environment,
            mediaquery: getCurrentBreakpoint(),
            country: site.country,
        },
        page: {
            type: getPageType(PAGE_TYPE),
            load: document.referrer ? 'navigate' : 'direct',
            path: resolvePagePath(),
        },
    });
}

/** Cart errors, theme messages, validation, newsletter, and member signup custom events. */
function errorEventHandler() {
    document.addEventListener('cart:error', (e) => {
        pushTextMessage(e?.detail?.data?.message || 'Error occurred');
    });
    document.addEventListener('theme:text_message', (e) => {
        pushTextMessage(e?.detail?.message);
    });
    document.addEventListener('theme:validation_message', (e) => {
        const text = typeof e?.detail?.validation_text === 'string' ? e.detail.validation_text.trim() : '';
        if (!text) return;
        window.dataLayer.push({
            event: 'validation_message',
            validation_text: text,
            validation_field: e.detail.validation_field || '',
        });
    });
    document.addEventListener('theme:newsletter_signup', (e) => pushNewsletterEvent('newsletter_signup', e?.detail));
    document.addEventListener('theme:newsletter_error', (e) => pushNewsletterEvent('newsletter_error', e?.detail));
    document.addEventListener('theme:member_signup', (e) => pushMemberSignupEvent(e?.detail));
}

/** Global `button_click` and `popup_cta` from elements marked in open dialogs. */
function generalButtonsClick() {
    document.addEventListener('click', (e) => {
        const el = e.target.closest(BUTTON_SELECTORS.join(', '));
        if (!el) return;
        if (el.dataset?.layerPopupCta) setDataLayerDialog('popup_cta', el.dataset.layerPopupCta);
        window.dataLayer.push({event: 'button_click', navigation_text: getElText(el)});
    });
}

/** `navigation_open` / `navigation_click` from header menu custom events. */
function navigationListeners() {
    document.addEventListener('navigation:open', ({detail}) => {
        const maincategory = detail?.maincategory;
        if (maincategory) window.dataLayer.push({event: 'navigation_open', maincategory});
    });
    document.addEventListener('navigation:click', ({detail}) => {
        const maincategory = detail?.maincategory?.trim();
        if (!maincategory) return;
        const subcategory = detail.subcategory?.trim();
        const subsubcategory = detail.subsubcategory?.trim();
        window.dataLayer.push({
            event: 'navigation_click',
            maincategory,
            ...(subcategory && {subcategory}),
            ...(subsubcategory && {subsubcategory}),
        });
    });
}

/** Minimum visible ratio before `view_promotion` fires for a hero banner. */
const VIEW_PROMOTION_THRESHOLD = 0.5;

/**
 * Parses promotion config from a hero root element.
 * @param {Element} root
 * @returns {{ creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string } | null}
 */
function parsePromotionConfig(root) {
    const script = root.querySelector('script[data-gtm-promotion-config]');
    if (!(script instanceof HTMLScriptElement)) return null;

    let data;
    try {
        data = JSON.parse(script.textContent || '{}');
    } catch {
        return null;
    }

    const promotion_id = (data.promotion_id || '').trim();
    const promotion_name = (data.promotion_name || '').trim();
    if (!promotion_id || !promotion_name) return null;

    return {
        creative_name: (data.creative_name || '').trim(),
        creative_slot: (data.creative_slot || '').trim(),
        promotion_id,
        promotion_name,
    };
}

/**
 * @returns {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>}
 */
function buildHeroPromotionCache() {
    /** @type {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>} */
    const promotionCache = new Map();

    for (const target of document.querySelectorAll('[data-gtm-view-promotion]')) {
        const promotion = parsePromotionConfig(target);
        if (promotion) promotionCache.set(target, promotion);
    }

    return promotionCache;
}

/**
 * @param {'view_promotion' | 'select_promotion'} eventName
 * @param {{ creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }} promotion
 */
function pushPromotionEvent(eventName, promotion) {
    resetEcommerce();
    window.dataLayer.push({event: eventName, ecommerce: promotion});
}

/**
 * @param {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>} promotionCache
 */
function initViewPromotionObserver(promotionCache) {
    let remaining = promotionCache.size;

    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;

                const root = entry.target;
                if (!(root instanceof Element)) continue;

                observer.unobserve(root);
                remaining -= 1;

                const promotion = promotionCache.get(root);
                if (promotion && root.dataset.gtmPromotionFired !== 'true') {
                    root.dataset.gtmPromotionFired = 'true';
                    pushPromotionEvent('view_promotion', promotion);
                }

                if (remaining === 0) observer.disconnect();
            }
        },
        {threshold: VIEW_PROMOTION_THRESHOLD},
    );

    promotionCache.forEach((_, target) => observer.observe(target));
}

/**
 * @param {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>} promotionCache
 */
function initSelectPromotionListener(promotionCache) {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-gtm-view-promotion] a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;

        const root = link.closest('[data-gtm-view-promotion]');
        const promotion = root ? promotionCache.get(root) : undefined;
        if (!promotion) return;

        pushPromotionEvent('select_promotion', promotion);
    });
}

/** Hero `view_promotion` (viewport) and `select_promotion` (link click). */
function initHeroPromotionTracking() {
    const promotionCache = buildHeroPromotionCache();
    if (!promotionCache.size) return;

    initViewPromotionObserver(promotionCache);
    initSelectPromotionListener(promotionCache);
}

/** `navigation_tile` on PLP carousel tile clicks (categories from header menu). */
function initNavigationTileListener() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('.resource-list__carousel a.collection-card__link[href]');
        if (!link) return;

        const menu = getHeaderMenuHierarchy();
        const [maincategory, subcategory = ''] = findMenuPathByHandle(menu?.items, collectionHandleFromUrl(link.href));
        if (!maincategory) return;

        const subsubcategory = link.querySelector('.visually-hidden')?.textContent?.trim() || '';

        window.dataLayer.push({event: 'navigation_tile', maincategory, subcategory, subsubcategory});
    });
}

/** `countrypicker_open` / `countrypicker_click` from localization components. */
function countryPickerListeners() {
    document.addEventListener('countrypicker:open', () => {
        window.dataLayer.push({event: 'countrypicker_open'});
    });
    document.addEventListener('countrypicker:click', ({detail}) => {
        const country = detail?.country?.trim();
        if (country) window.dataLayer.push({event: 'countrypicker_click', country});
    });
}

/** `contact` from form submit or mailto/tel/contact-page links. */
function contactListeners() {
    document.addEventListener('click', (e) => {
        const submitBtn = e.target.closest(
            'contact-form-component button[type="submit"], contact-form-component input[type="submit"]',
        );
        if (submitBtn) {
            const form = submitBtn.closest('form');
            if (form?.checkValidity()) window.dataLayer.push({event: 'contact', platform: 'form'});
            return;
        }
        const link = e.target.closest('a[href]');
        if (!link) return;
        const href = (link.getAttribute('href') || '').toLowerCase();
        const platform =
            (link.dataset.contactPlatform || '').trim().toLowerCase() ||
            (href.startsWith('mailto:') ? 'email' : '') ||
            (href.startsWith('tel:') ? 'phone' : '') ||
            (href.includes('/pages/contact') || /\/contact(\/|$|\?|#)/.test(href) ? 'contact_page' : '');
        if (platform) window.dataLayer.push({event: 'contact', platform});
    });
}

/** `navigation_footer_click` for footer anchor clicks. */
function footerNavigationClickListener() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('footer a[href]');
        if (!link) return;
        const navigation_text = getElText(link);
        if (navigation_text) window.dataLayer.push({event: 'navigation_footer_click', navigation_text});
    });
}

/** Observes cookie banner and Voyado quick-add for `popup_visible`. */
function popUpListener() {
    observerHandler('#shopify-pc__banner').then((el) => {
        if (isVisualPopUp(el)) setDataLayerPopUp('popup_visible', getIdPopUp(el));
    });

    observerHandler('.v-quick-add-modal-dialog').then((el) => {
        if (isVisualPopUp(el)) setDataLayerPopUp('popup_visible', getIdPopUp(el));
    });

    observerHandler('#usercentrics-cmp-ui').then((el) => {
        if (isVisualPopUp(el)) setDataLayerPopUp('popup_visible', getIdPopUp(el));
    });
}

/**
 * Applies major-unit prices on static PDP item JSON before `view_item`.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function formatItemPrices(item) {
    const price = formatPrice(item.price);
    const original_price = formatPrice(item.original_price);
    const rest = {...item};
    delete rest.index;
    return {
        ...rest,
        price,
        original_price,
        discount: Math.max(0, +(original_price - price).toFixed(2)),
    };
}

/**
 * Dynamic parent breadcrumb items on the PDP, excluding "Home" (first) and the current product
 * (last), which `.breadcrumbs__list` always renders statically.
 * @returns {Element[]}
 */
function getBreadcrumbParentItems() {
    return Array.from(document.querySelectorAll('.breadcrumbs__list .breadcrumbs__item')).slice(1, -1);
}

/**
 * Overrides `item_list_id`/`item_list_name` on a GA4 item with the immediate parent category from
 * the live PDP breadcrumb trail — server-rendered from the product's resolved menu collection
 * (see `sections/breadcrumbs.liquid`). Leaves the static value (`product.collections.first`, from
 * Liquid) untouched when breadcrumbs show no parent category, e.g. a product with no collection
 * linked in the breadcrumbs menu.
 * PDP `view_item` only.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function applyBreadcrumbListContext(item) {
    const lastParent = getBreadcrumbParentItems().pop();
    const href = lastParent?.querySelector('a.breadcrumbs__link')?.getAttribute('href') || '';
    const handle = collectionHandleFromUrl(href);
    const name = lastParent?.querySelector('[itemprop="name"]')?.textContent?.trim();
    if (!handle || !name) return item;

    item.item_list_id = handle;
    item.item_list_name = name;
    return item;
}

/**
 * PDP-only: `view_item`, variant change, gallery, reviews (Bazaarvoice).
 * Only registered when `GTMConfig.pageType === 'product'`.
 */
function initProductPageTracking() {
    const productId = cfg.productId;

    const gtmData = getGtmData(productId);
    if (gtmData) {
        const item = applyBreadcrumbListContext(formatItemPrices(gtmData));
        resetEcommerce();
        window.dataLayer.push({
            event: 'view_item',
            ecommerce: {currency: CURRENCY, value: item.price, items: [item]},
        });
    }

    document.addEventListener('variant:update', (e) => {
        const variant = e?.detail?.resource;
        if (!variant?.id) return;
        resetEcommerce();
        window.dataLayer.push({
            event: 'change_variant',
            product: variant.id,
            item_variant: variant.title ?? '',
        });
    });

    let lastPhotoZoomAt = 0;
    let lastPhotoZoomIndex = -1;

    const setDataLayerMediaGallery = (eventType, index) => {
        window.dataLayer.push({event: eventType, product: productId, index});
    };

    const pushPhotoZoom = (index) => {
        const now = Date.now();
        if (index === lastPhotoZoomIndex && now - lastPhotoZoomAt < 400) return;
        lastPhotoZoomIndex = index;
        lastPhotoZoomAt = now;
        setDataLayerMediaGallery('photo_zoom', index);
    };

    document.addEventListener('zoom-media:index', (e) => pushPhotoZoom(e.detail.index + 1));
    document.addEventListener('zoom-media:selected', (e) => pushPhotoZoom(e.detail.index + 1));
    document.addEventListener('zoom-media:thumbnail', (e) =>
        setDataLayerMediaGallery('photo_select', e.detail.index + 1),
    );

    document.addEventListener('slideshow:select', (e) => {
        const {slide, index, trigger, event} = e.detail;
        const i = index + 1;
        const gallery = slide?.closest('media-gallery');
        if (!gallery) return;
        const slideshow = gallery.querySelector('slideshow-component');
        const slideCount = slideshow?.slides?.length ?? 0;
        const isLastSlide = slideCount > 0 && index === slideCount - 1;

        if (trigger === 'scroll') setDataLayerMediaGallery('photo_scroll', i);
        if (event?.target.closest('slideshow-controls')) setDataLayerMediaGallery('photo_select', i);
        if (!event && trigger === 'select') setDataLayerMediaGallery('photo_select', i);
        if (event?.target.closest('slideshow-arrows')) {
            const action = event.target.closest('[ref]')?.getAttribute('ref');
            if (action === 'next' || action === 'previous') setDataLayerMediaGallery(`photo_${action}`, i);
        }
        if (isLastSlide) setDataLayerMediaGallery('photo_last', i);
    });

    document.addEventListener('click', (e) => {
        if (e.target.closest('[data-bv-show="inpage_submission"]')) return;
        if (!e.target.closest('[data-bv-show="rating_summary"], [data-bv-show="reviews"]')) return;
        window.dataLayer.push({event: 'review_click'});
    });

    const BV_SCOPE = '[data-bv-show="reviews"], [data-bv-show="inpage_submission"]';
    let currentReviewRating = null;

    document.addEventListener(
        'click',
        (event) => {
            const path = event.composedPath();
            const isBvArea = path.some((el) => el instanceof HTMLElement && el.matches(BV_SCOPE));
            if (!isBvArea) return;
            const ratingEl = path.find(
                (el) =>
                    el instanceof HTMLElement &&
                    (el.matches('[role="radio"]') || el.matches('[role="button"].bv-reviews-product-rating-star')),
            );
            if (!ratingEl) return;
            const rating = (ratingEl.getAttribute('aria-label') || ratingEl.textContent || '').match(/[1-5]/)?.[0];
            if (rating) currentReviewRating = Number(rating);
        },
        true,
    );

    const bvObserver = new MutationObserver(() => {
        if (!window.BV) return;
        bvObserver.disconnect();
        window.BV.configure('global', {
            events: {
                submissionSubmitted() {
                    const rating = currentReviewRating;
                    if (typeof rating !== 'number' || rating < 1 || rating > 5) return;
                    window.dataLayer.push({event: 'review_submit', review_rating: rating});
                },
            },
        });
    });
    bvObserver.observe(document.documentElement, {childList: true, subtree: true});
}

/**
 * Cart template: `view_cart` with CDP snapshot + GA4 `items[]` (same shape as `view_item`).
 *
 * Looked up by line position (`data-line-index`), not by `product_id`/variant `id` — two cart
 * lines can share the same variant (e.g. personalized items with different line properties),
 * which would collide on a `product_id`-`id` DOM id and make `getElementById` return the wrong line.
 *
 * @param {number} index - 0-based position of this line in `cart.items`.
 */
function getGtmCartLineData(index) {
    const el = document.querySelector(`[data-line-index="${index}"]`);
    return parseGtmJsonElement(el, `gtm-cart-line[${index}]`);
}

/** GA4 `items[]` for cart page `view_cart` — reads `[data-line-index]`, formats like `view_item`. */
function buildViewCartItems(cart) {
    return (cart?.items || [])
        .map((line, index) => {
            const gtmData = getGtmCartLineData(index);
            if (!gtmData) return null;
            return applyMenuListContext({...formatItemPrices(gtmData), quantity: line.quantity ?? 1});
        })
        .filter(Boolean);
}

/**
 * Captures a cart line's `#gtm-cart-line-*` JSON at the moment its remove button is clicked
 * — before the theme's own click handler runs, so before its section morph can remove that
 * DOM node. Listens on `document` in the capture phase so it always runs first, regardless of
 * where `cart-items-component` attaches its own (bubbling-phase) listener.
 */
function captureCartLineOnRemoveClick() {
    document.addEventListener(
        'click',
        (e) => {
            const button = e.target.closest('.cart-items__remove');
            if (!button) return;
            const key = button.closest('[data-key]')?.dataset.key;
            if (!key) return;
            const gtmData = parseGtmJsonElement(document.querySelector(`[data-line-key="${key}"]`), `gtm-cart-line[key=${key}]`);
            pruneStaleClickCaptures();
            if (gtmData) clickCapturedLineData.set(key, {data: gtmData, capturedAt: Date.now()});
        },
        {capture: true},
    );
}

/** `view_cart` from the current `shopifyCart` state — cart page load, or cart drawer open. */
function pushViewCartEvent() {
    resetEcommerce();
    window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
            currency: shopifyCart?.currency || CURRENCY,
            ...buildCartCdpEcommerce(shopifyCart),
            items: buildViewCartItems(shopifyCart),
        },
    });
}

function initCartPageTracking() {
    pushViewCartEvent();
}

function initNewsletterDoiTracking() {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    if (success === null) return;

    if (success !== '1') {
        console.warn('[GTM] newsletter_doi not fired: confirmation was not successful');
        return;
    }

    const consumerId = params.get('consumerId');
    const emailHash = params.get('email_hash');

    if (!consumerId) {
        console.warn('[GTM] newsletter_doi not fired: missing consumerId param');
        return;
    }

    if (!emailHash) {
        console.warn('[GTM] newsletter_doi not fired: missing email_hash param');
        return;
    }

    window.dataLayer.push({
        event: 'newsletter_doi',
        form_id: 'footer_newsletter',
        email: emailHash,
        crm_id: consumerId,
    });
}

/** Registers listeners shared on every template; branches for cart and PDP. */
function initGlobalTracking() {
    pushDataLayerEvents();
    errorEventHandler();
    generalButtonsClick();
    navigationListeners();
    countryPickerListeners();
    contactListeners();
    footerNavigationClickListener();
    popUpListener();
    initHeroPromotionTracking();
    captureCartLineOnRemoveClick();

    document.addEventListener('dialog:open', (e) => {
        const {dialog} = e.detail;
        markButtons(dialog);
        setDataLayerDialog('popup_visible', getDialogId(dialog));
        if (dialog.classList.contains('cart-drawer__dialog')) onCartSettled(pushViewCartEvent);
    });

    document.addEventListener('dialog:close', (e) => {
        setDataLayerDialog('popup_close', getDialogId(e.detail.dialog));
    });

    if (PAGE_TYPE === 'cart') initCartPageTracking();
    if (PAGE_TYPE === 'product') initProductPageTracking();
    if (PAGE_TYPE === 'collection') initNavigationTileListener();
    if (PAGE_TYPE === 'page') initNewsletterDoiTracking();
}

if (sessionStorage.getItem(PENDING_CART_EVENT)) {
    fetchCartData();
}

if (!window.__GTM_patchedFetch) {
    window.__GTM_patchedFetch = true;
    patchFetch();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGlobalTracking);
} else {
    initGlobalTracking();
}
